import { OutlierGuardConfig } from './types'

/**
 * Cross-source consensus: the one signal that works on oracles exposing no
 * timestamp at all (Compound V2 forks return a bare `getUnderlyingPrice`,
 * Morpho a bare `price()`), where a dead feed is indistinguishable from a
 * live one on-chain.
 *
 * Shared by the selection guard and by the feed-reliability stats so BOTH
 * apply the same standard of proof. Learning "this feed disagrees" from a
 * two-source group would be the same mistake as rejecting on it.
 */

export const DEFAULT_OUTLIER_GUARD: OutlierGuardConfig = {
  minAgreeingFamilies: 3,
  agreementTolerance: 0.05,
  rejectFactor: 2,
}

export function median(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b)
  const mid = Math.floor(sorted.length / 2)
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2
}

/** Anything carrying a price and the identity of the source behind it. */
export interface FeedPriced {
  priceUSD: number
  feedKey: string
}

/**
 * The reference price a group agrees on, or `undefined` when the group has
 * no broad agreement.
 *
 * The reference is the median of PER-FEED medians, so the five protocols
 * reading one Chainlink aggregator count once, not five times, and a
 * protocol emitting a price per market cannot outvote everyone else.
 *
 * Requiring a real cluster is what stops a minority of broken feeds from
 * overruling a correct price: where two dead feeds quote a `1e-8` sentinel
 * and only AAVE quotes the truth, no cluster forms and the caller is told
 * there is no consensus — rather than being handed `1e-8` as one.
 */
export function consensusReference(
  candidates: FeedPriced[],
  guard: OutlierGuardConfig = DEFAULT_OUTLIER_GUARD,
): number | undefined {
  if (candidates.length < guard.minAgreeingFamilies) return undefined

  const perFeed = new Map<string, number[]>()
  for (const c of candidates) {
    const bucket = perFeed.get(c.feedKey)
    if (bucket) bucket.push(c.priceUSD)
    else perFeed.set(c.feedKey, [c.priceUSD])
  }
  if (perFeed.size < guard.minAgreeingFamilies) return undefined

  const feedPrices = [...perFeed.values()].map(median)
  const reference = median(feedPrices)
  if (!(reference > 0)) return undefined

  const agreeing = feedPrices.filter(
    (p) => Math.abs(p / reference - 1) <= guard.agreementTolerance,
  ).length
  if (agreeing < guard.minAgreeingFamilies) return undefined

  return reference
}

/**
 * Drop candidates that contradict a strong cross-feed consensus.
 *
 * Rejects on a FACTOR (default >2x off), never on a percentage, so ordinary
 * oracle disagreement is untouched — and never empties a group.
 *
 * Exported so tests can assert against the real implementation instead of a
 * copy that can drift from it.
 */
export function rejectOutliers<T extends FeedPriced>(
  candidates: T[],
  guard: OutlierGuardConfig = DEFAULT_OUTLIER_GUARD,
): T[] {
  const reference = consensusReference(candidates, guard)
  if (reference === undefined) return candidates

  const upper = reference * guard.rejectFactor
  const lower = reference / guard.rejectFactor
  const kept = candidates.filter(
    (c) => c.priceUSD <= upper && c.priceUSD >= lower,
  )
  return kept.length > 0 ? kept : candidates
}
